import { MessageSquare, Mic, Clock, CheckCircle, User, Bot, TestTube } from 'lucide-react';
import SocialShare from './SocialShare';
import type { FeedItemProps } from '../types/interactions';

export default function FeedItem({ message, onShare, showShareButtons = true }: FeedItemProps) {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  };

  const getMessageTypeIcon = () => {
    switch (message.message_type) {
      case 'audio':
        return <Mic className="h-4 w-4 text-purple-500" />;
      case 'morning_prompt':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'evening_checkin':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'test':
        return <TestTube className="h-4 w-4 text-blue-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
    }
  };

  const getMessageTypeLabel = () => {
    switch (message.message_type) {
      case 'morning_prompt':
        return 'Morning Prompt';
      case 'evening_checkin':
        return 'Evening Check-in';
      case 'follow_up':
        return 'Follow-up';
      case 'audio':
        return 'Voice Message';
      case 'test':
        return 'Test Message';
      default:
        return 'Message';
    }
  };

  const isFromUser = message.role === 'user';
  const isFromAssistant = message.role === 'assistant';
  const isSampleData = message.id.startsWith('sample-');

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 hover:shadow-md transition-shadow relative">
      {/* Sample Data Indicator */}
      {isSampleData && (
        <div className="absolute top-2 right-2">
          <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
            Sample Data
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {getMessageTypeIcon()}
            <span className="text-sm font-medium text-gray-700">
              {getMessageTypeLabel()}
            </span>
          </div>
          
          {(isFromUser || isFromAssistant) && (
            <div className="flex items-center space-x-1">
              {isFromUser ? (
                <User className="h-4 w-4 text-gray-500" />
              ) : (
                <Bot className="h-4 w-4 text-pink-500" />
              )}
              <span className="text-xs text-gray-500">
                {isFromUser ? 'You' : 'AI Coach'}
              </span>
            </div>
          )}
        </div>
        
        <time className="text-sm text-gray-500" dateTime={message.sent_at}>
          {formatTime(message.sent_at)}
        </time>
      </div>

      {/* Message Content */}
      <div className="space-y-3">
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
            {message.message_content}
          </p>
        </div>

        {/* Image Content */}
        {message.has_image && message.image_url && (
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <img
              src={message.image_url}
              alt="Message attachment"
              className="w-full h-auto max-h-96 object-cover"
              loading="lazy"
              onError={(e) => {
                // Fallback for broken images
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      {/* Social Share */}
      {showShareButtons && onShare && message.has_image && (
        <div className="pt-3 border-t border-gray-100">
          <SocialShare
            message={message}
            onShare={onShare}
          />
        </div>
      )}
    </div>
  );
}